import React, { useEffect, useState } from "react";
import { Checkbox, CircularProgress, FormControlLabel, TextField } from "@material-ui/core";
import styled from "styled-components";
import { MyBookingWrapper } from "../MyBooking/styled/DesStyled";
import useGetButtonEmailContent from "./hook/useGetButton";
import useEmailKeywork from "./hook/useGetEmailKeywork";
import DialogUpdateContentEmail from "./DialogUpdateContentEmail";
import OrderEmailSubjectTemplatesPanel from "./OrderEmailSubjectTemplatesPanel";
import {
	getStationRequestEmailConfig,
	saveStationRequestEmailConfig,
	StationRequestEmailConfig,
} from "../../../services/StationRequestEmailConfigService";
import {
	getStationLandingNotFoundAlertConfig,
	saveStationLandingNotFoundAlertConfig,
	type StationLandingNotFoundAlertConfig,
} from "../../../services/StationLandingNotFoundAlertConfigService";
import PermissionGate from "../../controls/auth/PermissionGate";

type TemplateGroupKey =
	| "booking-subjects"
	| "booking-paid"
	| "booking-updated"
	| "booking-cancel"
	| "station-request"
	| "finance";

type TemplateMeta = {
	group: TemplateGroupKey;
	audience: string;
	description: string;
};

const TEMPLATE_META: Record<number, TemplateMeta> = {
	1: {
		group: "booking-paid",
		audience: "Customer",
		description: "Email xác nhận booking thành công gửi đến khách hàng.",
	},
	2: {
		group: "booking-paid",
		audience: "Admin",
		description: "Email nội bộ để admin nắm booking mới đã thanh toán.",
	},
	3: {
		group: "booking-paid",
		audience: "Station",
		description: "Email gửi cho station để chuẩn bị nhận booking mới.",
	},
	4: {
		group: "booking-cancel",
		audience: "Customer",
		description: "Email thông báo booking bị hủy gửi tới khách hàng.",
	},
	5: {
		group: "booking-cancel",
		audience: "Admin",
		description: "Email nội bộ để admin theo dõi các booking đã hủy.",
	},
	6: { group: "booking-cancel", audience: "Station", description: "Email thông báo hủy booking gửi tới station." },
	7: {
		group: "finance",
		audience: "Station",
		description: "Email xác nhận payment request đã được gửi, gửi cho station.",
	},
	8: { group: "finance", audience: "Station", description: "Email payout / payment finalized gửi cho station." },
	9: {
		group: "station-request",
		audience: "Station",
		description: "Email xác nhận đã nhận đơn đăng ký, gửi cho đối tác vừa submit form đăng ký mở station mới.",
	},
	10: {
		group: "station-request",
		audience: "Station",
		description: "Email thông báo station request đã được duyệt.",
	},
	11: { group: "station-request", audience: "Station", description: "Email thông báo station request bị từ chối." },
	12: {
		group: "station-request",
		audience: "Admin",
		description: "Email nội bộ báo admin có station request mới cần review.",
	},
	13: {
		group: "booking-updated",
		audience: "Customer",
		description: "Generic — gửi khách khi update có nhiều thay đổi (fallback).",
	},
	14: {
		group: "booking-updated",
		audience: "Admin",
		description: "Generic — admin khi update có nhiều thay đổi (fallback).",
	},
	15: {
		group: "booking-updated",
		audience: "Station",
		description: "Generic — gửi station khi update có nhiều thay đổi (fallback).",
	},
	16: { group: "booking-updated", audience: "Customer", description: "Email gửi khách hàng khi đổi Date." },
	17: { group: "booking-updated", audience: "Admin", description: "Email nội bộ khi đổi Date." },
	18: { group: "booking-updated", audience: "Station", description: "Email gửi station khi đổi Date." },
	22: { group: "booking-updated", audience: "Customer", description: "Email gửi khách hàng khi đổi Address (trạm)." },
	23: { group: "booking-updated", audience: "Admin", description: "Email nội bộ khi đổi Address." },
	33: { group: "finance", audience: "Station", description: "Email gửi station khi Admin duyệt yêu cầu rút tiền." },
	34: { group: "finance", audience: "Station", description: "Email gửi station khi Admin từ chối yêu cầu rút tiền." },
};

const GROUP_CONFIG: Array<{ key: TemplateGroupKey; label: string }> = [
	{ key: "booking-paid", label: "Booking Paid" },
	{ key: "booking-updated", label: "Booking Updated" },
	{ key: "booking-cancel", label: "Booking Canceled" },
	{ key: "station-request", label: "Station Request" },
	{ key: "finance", label: "Finance" },
];

const AUDIENCE_COLOR: Record<string, { bg: string; text: string }> = {
	Customer: { bg: "#ecfdf5", text: "#0f766e" },
	Admin: { bg: "#eff6ff", text: "#1d4ed8" },
	Station: { bg: "#f5f3ff", text: "#7c3aed" },
};

/* ─── Layout ──────────────────────────────────────────────────── */

const Shell = styled(MyBookingWrapper)`
	padding: 20px 0 40px;
`;

const Inner = styled.div`
	width: 1200px;
	max-width: 100%;
	margin: 0 auto;

	@media (max-width: 1282px) {
		width: 100%;
		padding: 0 16px;
	}
`;

const PageHeader = styled.div`
	margin-bottom: 20px;
`;

const PageTitle = styled.h1`
	margin: 0;
	font-size: 22px;
	font-weight: 700;
	color: #111827;
`;

const PageMeta = styled.div`
	font-size: 13px;
	color: #6b7280;
	margin-top: 4px;
`;

/* ─── Tabs ────────────────────────────────────────────────────── */

const TabBar = styled.div`
	display: flex;
	gap: 6px;
	flex-wrap: wrap;
	margin-bottom: 20px;
	padding-bottom: 16px;
	border-bottom: 1px solid #e5e7eb;
`;

const Tab = styled.button<{ $active: boolean }>`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 16px;
	border-radius: 10px;
	border: 1px solid ${({ $active }) => ($active ? "#2563eb" : "#e5e7eb")};
	background: ${({ $active }) => ($active ? "#eff6ff" : "#ffffff")};
	color: ${({ $active }) => ($active ? "#1d4ed8" : "#374151")};
	font-size: 14px;
	font-weight: ${({ $active }) => ($active ? "700" : "500")};
	cursor: pointer;
	transition: all 0.15s ease;
	white-space: nowrap;

	&:hover {
		border-color: #93c5fd;
		background: #f8fbff;
		color: #1d4ed8;
	}
`;

const TabCount = styled.span<{ $active: boolean }>`
	padding: 1px 7px;
	border-radius: 999px;
	font-size: 11px;
	font-weight: 700;
	background: ${({ $active }) => ($active ? "#dbeafe" : "#f3f4f6")};
	color: ${({ $active }) => ($active ? "#1d4ed8" : "#6b7280")};
`;

/* ─── Grid ────────────────────────────────────────────────────── */

const Grid = styled.div`
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 14px;

	@media (max-width: 1024px) {
		grid-template-columns: repeat(2, 1fr);
	}
	@media (max-width: 640px) {
		grid-template-columns: 1fr;
	}
`;

const Card = styled.button<{ $accent: string; $active: boolean }>`
	display: flex;
	flex-direction: column;
	gap: 10px;
	width: 100%;
	text-align: left;
	padding: 16px;
	border-radius: 16px;
	border: 1.5px solid ${({ $active, $accent }) => ($active ? $accent : "#e5e7eb")};
	background: ${({ $active }) => ($active ? "#f8fbff" : "#ffffff")};
	box-shadow: ${({ $active, $accent }) => ($active ? `0 0 0 3px ${$accent}22` : "0 2px 8px rgba(15,23,42,0.04)")};
	cursor: pointer;
	transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;

	&:hover {
		border-color: ${({ $accent }) => $accent};
		transform: translateY(-1px);
		box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
	}
`;

const CardTop = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const AudienceBadge = styled.span<{ $bg: string; $text: string }>`
	padding: 3px 10px;
	border-radius: 999px;
	font-size: 11px;
	font-weight: 700;
	letter-spacing: 0.05em;
	text-transform: uppercase;
	background: ${({ $bg }) => $bg};
	color: ${({ $text }) => $text};
`;

const CardId = styled.span`
	font-size: 12px;
	font-weight: 600;
	color: #9ca3af;
`;

const CardName = styled.div`
	font-size: 15px;
	font-weight: 700;
	color: #111827;
	line-height: 1.35;
`;

const CardDesc = styled.div`
	font-size: 13px;
	color: #6b7280;
	line-height: 1.5;
`;

const CardFooter = styled.div<{ $accent: string }>`
	margin-top: auto;
	padding-top: 10px;
	border-top: 1px solid #f1f5f9;
	font-size: 12px;
	font-weight: 700;
	color: ${({ $accent }) => $accent};
	text-align: right;
`;

const EmptyMsg = styled.div`
	padding: 40px;
	text-align: center;
	color: #9ca3af;
	font-size: 14px;
	background: #ffffff;
	border-radius: 16px;
	border: 1px dashed #e5e7eb;
`;

const SettingsPanel = styled.div`
	margin-bottom: 20px;
	padding: 16px 18px;
	border: 1px solid #e5e7eb;
	border-radius: 16px;
	background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
`;

const SettingsHeader = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 12px;

	@media (max-width: 768px) {
		flex-direction: column;
	}
`;

const SettingsTitle = styled.div`
	font-size: 15px;
	font-weight: 700;
	color: #111827;
`;

const SettingsText = styled.div`
	font-size: 13px;
	line-height: 1.5;
	color: #6b7280;
	margin-top: 4px;
`;

const SettingsRow = styled.div`
	display: flex;
	align-items: flex-end;
	gap: 12px;

	@media (max-width: 768px) {
		flex-direction: column;
		align-items: stretch;
	}
`;

const SettingsField = styled.div`
	flex: 1;
`;

const SaveButton = styled.button`
	height: 40px;
	padding: 0 18px;
	border: none;
	border-radius: 10px;
	background: #2563eb;
	color: #ffffff;
	font-size: 14px;
	font-weight: 700;
	cursor: pointer;
	transition: background 0.15s ease;

	&:hover:not(:disabled) {
		background: #1d4ed8;
	}

	&:disabled {
		background: #93c5fd;
		cursor: not-allowed;
	}
`;

const StatusText = styled.div<{ $error?: boolean }>`
	font-size: 12px;
	margin-top: 10px;
	color: ${({ $error }) => ($error ? "#dc2626" : "#2563eb")};
`;

/* ─── Component ───────────────────────────────────────────────── */

type TemplateItem = { id: number; name: string; subject?: string; meta: TemplateMeta };

function buildItems(button?: Array<{ id?: number; name?: string; subject?: string }>): TemplateItem[] {
	return (button || [])
		.filter((b) => b?.id && b?.name)
		.map((b) => ({
			id: b.id as number,
			name: b.name as string,
			subject: typeof b.subject === "string" ? b.subject : undefined,
			meta: TEMPLATE_META[b.id as number] ?? {
				group: "finance" as TemplateGroupKey,
				audience: "Admin",
				description: "",
			},
		}))
		.sort((a, b) => a.id - b.id);
}

export default function AdminFormatEmail() {
	const { button, reload } = useGetButtonEmailContent();
	const { keywork } = useEmailKeywork();
	const [activeGroup, setActiveGroup] = useState<TemplateGroupKey>("booking-paid");
	const [openDialog, setOpenDialog] = useState(false);
	const [selectedId, setSelectedId] = useState<number | null>(null);
	const [stationRequestEmailConfig, setStationRequestEmailConfig] = useState<StationRequestEmailConfig | null>(null);
	const [loadingStationRequestConfig, setLoadingStationRequestConfig] = useState(false);
	const [savingStationRequestConfig, setSavingStationRequestConfig] = useState(false);
	const [stationRequestConfigFeedback, setStationRequestConfigFeedback] = useState<{
		message: string;
		isError: boolean;
	} | null>(null);

	const [landing404Config, setLanding404Config] = useState<StationLandingNotFoundAlertConfig | null>(null);
	const [loadingLanding404Config, setLoadingLanding404Config] = useState(false);
	const [savingLanding404Config, setSavingLanding404Config] = useState(false);
	const [landing404Feedback, setLanding404Feedback] = useState<{ message: string; isError: boolean } | null>(null);

	const all = buildItems(button);
	const selectedTemplate = all.find((item) => item.id === selectedId) || null;
	const grouped = Object.fromEntries(
		GROUP_CONFIG.map(({ key }) => [key, all.filter((t) => t.meta.group === key)])
	) as Record<TemplateGroupKey, TemplateItem[]>;

	const visible = grouped[activeGroup] || [];

	useEffect(() => {
		let mounted = true;

		(async () => {
			try {
				setLoadingStationRequestConfig(true);
				const config = await getStationRequestEmailConfig();
				if (!mounted) return;
				setStationRequestEmailConfig(config);
			} catch (error: any) {
				if (!mounted) return;
				setStationRequestConfigFeedback({
					message:
						error?.response?.data?.message ||
						error?.message ||
						"Không tải được cấu hình station request email.",
					isError: true,
				});
			} finally {
				if (mounted) {
					setLoadingStationRequestConfig(false);
				}
			}
		})();

		return () => {
			mounted = false;
		};
	}, []);

	useEffect(() => {
		let mounted = true;

		(async () => {
			try {
				setLoadingLanding404Config(true);
				const cfg = await getStationLandingNotFoundAlertConfig();
				if (!mounted) return;
				setLanding404Config(cfg);
			} catch (error: any) {
				if (!mounted) return;
				setLanding404Feedback({
					message:
						error?.response?.data?.message ||
						error?.message ||
						"Không tải được cấu hình cảnh báo station landing 404.",
					isError: true,
				});
			} finally {
				if (mounted) {
					setLoadingLanding404Config(false);
				}
			}
		})();

		return () => {
			mounted = false;
		};
	}, []);

	const handleOpen = (id: number) => {
		setSelectedId(id);
		setOpenDialog(true);
	};

	const handleStationRequestConfigChange = (key: keyof StationRequestEmailConfig, value: string) => {
		setStationRequestEmailConfig((prev) => (prev ? { ...prev, [key]: value } : prev));
		setStationRequestConfigFeedback(null);
	};

	const handleSaveLanding404Config = async () => {
		if (!landing404Config) {
			return;
		}
		try {
			setSavingLanding404Config(true);
			setLanding404Feedback(null);
			const dedupeSeconds = Math.round(Number(landing404Config.dedupeSeconds));
			const normalized: StationLandingNotFoundAlertConfig = {
				...landing404Config,
				dedupeSeconds: Number.isFinite(dedupeSeconds) && dedupeSeconds >= 60 ? dedupeSeconds : 3600,
			};
			await saveStationLandingNotFoundAlertConfig(normalized);
			setLanding404Feedback({
				message: "Đã lưu cấu hình email cảnh báo station landing 404.",
				isError: false,
			});
		} catch (error: any) {
			setLanding404Feedback({
				message: error?.response?.data?.message || error?.message || "Lưu cấu hình thất bại.",
				isError: true,
			});
		} finally {
			setSavingLanding404Config(false);
		}
	};

	const handleSaveStationRequestConfig = async () => {
		if (!stationRequestEmailConfig) {
			return;
		}
		try {
			setSavingStationRequestConfig(true);
			setStationRequestConfigFeedback(null);
			await saveStationRequestEmailConfig(stationRequestEmailConfig);
			setStationRequestConfigFeedback({
				message: "Đã lưu cấu hình email station request.",
				isError: false,
			});
		} catch (error: any) {
			setStationRequestConfigFeedback({
				message:
					error?.response?.data?.message || error?.message || "Lưu cấu hình email station request thất bại.",
				isError: true,
			});
		} finally {
			setSavingStationRequestConfig(false);
		}
	};

	return (
		<Shell>
			<Inner>
				<PageHeader>
					<PageTitle>Cấu hình Format Email</PageTitle>
					<PageMeta>{all.length} template</PageMeta>
				</PageHeader>

				<SettingsPanel style={{ marginBottom: 24 }}>
					<SettingsHeader>
						<div>
							<SettingsTitle>Station landing — cảnh báo 404</SettingsTitle>
							<SettingsText>
								Khi khách vào URL <code>/station/&lt;destination&gt;/&lt;partnerId&gt;</code> mà trang
								không hợp lệ (partner không tồn tại / sai destination), hệ thống có thể gửi email cho
								team vận hành. Bật và nhập danh sách email nhận (phân tách bằng dấu phẩy). Giới hạn
								trùng lặp: cùng một partner + destination + lý do trong khoảng thời gian (giây) chỉ gửi
								một lần.
							</SettingsText>
						</div>
					</SettingsHeader>

					{loadingLanding404Config ? (
						<div style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 40 }}>
							<CircularProgress size={18} />
							<span style={{ fontSize: 13, color: "#6b7280" }}>Đang tải cấu hình...</span>
						</div>
					) : (
						<>
							<div style={{ marginBottom: 12 }}>
								<FormControlLabel
									control={
										<Checkbox
											checked={!!landing404Config?.enabled}
											onChange={(_, checked) =>
												setLanding404Config((prev) =>
													prev ? { ...prev, enabled: checked } : prev
												)
											}
											color="primary"
											disabled={savingLanding404Config || !landing404Config}
										/>
									}
									label="Bật gửi email khi station landing trả về 404"
								/>
							</div>
							<div style={{ display: "grid", gap: 12 }}>
								<SettingsField>
									<TextField
										value={landing404Config?.recipientEmails || ""}
										onChange={(e) =>
											setLanding404Config((prev) =>
												prev ? { ...prev, recipientEmails: e.target.value } : prev
											)
										}
										variant="outlined"
										size="small"
										fullWidth
										label="Email nhận cảnh báo"
										placeholder="ops@company.com, dev@company.com"
										disabled={savingLanding404Config || !landing404Config}
									/>
								</SettingsField>
								<SettingsField>
									<TextField
										value={
											landing404Config?.dedupeSeconds != null
												? String(landing404Config.dedupeSeconds)
												: "3600"
										}
										onChange={(e) => {
											const v = parseInt(e.target.value, 10);
											setLanding404Config((prev) =>
												prev
													? {
															...prev,
															dedupeSeconds: Number.isFinite(v) ? v : prev.dedupeSeconds,
													  }
													: prev
											);
										}}
										variant="outlined"
										size="small"
										fullWidth
										label="Khoảng dedupe (giây)"
										helperText="60–86400. Trong khoảng này, cùng một URL lỗi chỉ trigger một email."
										disabled={savingLanding404Config || !landing404Config}
									/>
								</SettingsField>
								<SettingsRow>
									<PermissionGate permission="settings.manage">
										<SaveButton
											type="button"
											onClick={handleSaveLanding404Config}
											disabled={savingLanding404Config || !landing404Config}>
											{savingLanding404Config ? "Đang lưu..." : "Lưu cấu hình"}
										</SaveButton>
									</PermissionGate>
								</SettingsRow>
							</div>
							{landing404Feedback && (
								<StatusText $error={landing404Feedback.isError}>
									{landing404Feedback.message}
								</StatusText>
							)}
						</>
					)}
				</SettingsPanel>

				<TabBar>
					{GROUP_CONFIG.map(({ key, label }) => (
						<Tab
							key={key}
							type="button"
							$active={activeGroup === key}
							onClick={() => setActiveGroup(key)}>
							{label}
							{key !== "booking-subjects" ? (
								<TabCount $active={activeGroup === key}>{grouped[key].length}</TabCount>
							) : null}
						</Tab>
					))}
				</TabBar>

				{activeGroup === "booking-subjects" && <OrderEmailSubjectTemplatesPanel />}

				{activeGroup === "station-request" && (
					<SettingsPanel>
						<SettingsHeader>
							<div>
								<SettingsTitle>Station Request Email Config</SettingsTitle>
								<SettingsText>
									Cấu hình riêng email nhận thêm và email nhận thông báo admin cho luồng station
									request. Có thể nhập nhiều email, phân tách bằng dấu phẩy, chấm phẩy hoặc xuống
									dòng.
								</SettingsText>
							</div>
						</SettingsHeader>

						{loadingStationRequestConfig ? (
							<div style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 40 }}>
								<CircularProgress size={18} />
								<span style={{ fontSize: 13, color: "#6b7280" }}>
									Đang tải cấu hình station request...
								</span>
							</div>
						) : (
							<>
								<div style={{ display: "grid", gap: 12 }}>
									<SettingsField>
										<TextField
											value={stationRequestEmailConfig?.recipientEmails || ""}
											onChange={(e) =>
												handleStationRequestConfigChange("recipientEmails", e.target.value)
											}
											variant="outlined"
											size="small"
											fullWidth
											label="Email nhận thêm cho mail station request"
											placeholder="ops@whalelo.net, finance@whalelo.net"
											disabled={savingStationRequestConfig || !stationRequestEmailConfig}
										/>
									</SettingsField>
									<SettingsField>
										<TextField
											value={stationRequestEmailConfig?.adminNotificationRecipients || ""}
											onChange={(e) =>
												handleStationRequestConfigChange(
													"adminNotificationRecipients",
													e.target.value
												)
											}
											variant="outlined"
											size="small"
											fullWidth
											label="Email nhận thông báo admin có station request mới"
											placeholder="info@whalelo.net"
											disabled={savingStationRequestConfig || !stationRequestEmailConfig}
										/>
									</SettingsField>
									<SettingsRow>
										<SettingsText>
											Email nhận thêm sẽ được đưa vào danh sách người nhận trực tiếp của mail
											submit, approval, denial. Email nhận thông báo admin dùng cho template #12.
										</SettingsText>
										<PermissionGate permission="settings.manage">
											<SaveButton
												type="button"
												onClick={handleSaveStationRequestConfig}
												disabled={savingStationRequestConfig || !stationRequestEmailConfig}>
												{savingStationRequestConfig ? "Đang lưu..." : "Lưu cấu hình"}
											</SaveButton>
										</PermissionGate>
									</SettingsRow>
								</div>
								{stationRequestConfigFeedback && (
									<StatusText $error={stationRequestConfigFeedback.isError}>
										{stationRequestConfigFeedback.message}
									</StatusText>
								)}
							</>
						)}
					</SettingsPanel>
				)}

				{activeGroup !== "booking-subjects" && visible.length ? (
					<Grid>
						{visible.map((item) => {
							const ac = AUDIENCE_COLOR[item.meta.audience] ?? { bg: "#f3f4f6", text: "#374151" };
							return (
								<Card
									key={item.id}
									type="button"
									$accent={ac.text}
									$active={selectedId === item.id}
									onClick={() => handleOpen(item.id)}>
									<CardTop>
										<AudienceBadge
											$bg={ac.bg}
											$text={ac.text}>
											{item.meta.audience}
										</AudienceBadge>
										<CardId>#{item.id}</CardId>
									</CardTop>
									<CardName>{item.name}</CardName>
									<CardDesc>{item.meta.description}</CardDesc>
									<CardFooter $accent={ac.text}>Mở editor →</CardFooter>
								</Card>
							);
						})}
					</Grid>
				) : null}

				{activeGroup !== "booking-subjects" && !visible.length ? (
					<EmptyMsg>Chưa tải được danh sách template.</EmptyMsg>
				) : null}
			</Inner>

			<DialogUpdateContentEmail
				openDialog={openDialog}
				setOpenDialog={setOpenDialog}
				keywork={keywork}
				selectedId={selectedId}
				selectedTemplate={selectedTemplate}
				reloadTemplates={reload}
			/>
		</Shell>
	);
}
